import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { AdminLayout } from '../components/AdminLayout.js'
import { AdminApiError } from '../adminApi.js'
import { useAccount, useChangePassword } from '../hooks/useAdmins.js'
import { useAdminMe } from '../hooks/useAdminMe.js'
import { fmtDateTime } from '../format.js'
import { PasswordInput } from '../../shared/PasswordInput.js'

interface ChangePasswordForm {
  current_password: string
  new_password: string
  confirm_password: string
}

/**
 * Self-service account screen. Shows the logged-in admin's email + last login
 * (read-only) and a change-password form. The form requires the current
 * password; a successful change clears `must_change_password`.
 */
export function Account() {
  const account = useAccount()
  const me = useAdminMe()
  const changePassword = useChangePassword()

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordForm>()

  const newPassword = watch('new_password')

  function onSubmit(values: ChangePasswordForm) {
    if (values.new_password.length < 12) {
      toast.error('New password must be at least 12 characters.')
      return
    }
    if (values.new_password !== values.confirm_password) {
      toast.error('New password and confirmation do not match.')
      return
    }
    if (values.new_password === values.current_password) {
      toast.error('New password must differ from the current one.')
      return
    }
    changePassword.mutate(
      {
        current_password: values.current_password,
        new_password: values.new_password,
      },
      {
        onSuccess: () => {
          toast.success('Password changed.')
          reset()
        },
        onError: (err) =>
          toast.error(
            err instanceof AdminApiError
              ? err.userMessage
              : 'Could not change password.',
          ),
      },
    )
  }

  const admin = account.data?.admin
  const mustChange = me.data?.admin && account.data?.admin.must_change_password

  return (
    <AdminLayout>
      <div className="admin-page-head">
        <h1>Account</h1>
      </div>

      {mustChange && (
        <div className="admin-inline-error">
          Your password is temporary — set a new one below before continuing.
        </div>
      )}

      <div className="admin-section">
        <h3>Profile</h3>
        <div className="admin-card">
          {account.isLoading ? (
            <p className="admin-audit-meta">Loading…</p>
          ) : account.isError ? (
            <p className="admin-audit-meta">Couldn't load your account.</p>
          ) : (
            <dl className="admin-dl">
              <dt>Name</dt>
              <dd>{admin?.name ?? '—'}</dd>
              <dt>Email</dt>
              <dd>{admin?.email ?? '—'}</dd>
              <dt>Last login</dt>
              <dd>{fmtDateTime(admin?.last_login_at)}</dd>
            </dl>
          )}
        </div>
      </div>

      <div className="admin-section">
        <h3>Change password</h3>
        <div className="admin-card" style={{ maxWidth: 440 }}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="admin-field">
              <label className="admin-label" htmlFor="current_password">
                Current password
              </label>
              <PasswordInput
                id="current_password"
                classPrefix="admin"
                autoComplete="current-password"
                aria-invalid={errors.current_password ? 'true' : 'false'}
                {...register('current_password', {
                  required: 'Enter your current password',
                })}
              />
              {errors.current_password && (
                <p className="admin-field-error">
                  {errors.current_password.message}
                </p>
              )}
            </div>

            <div className="admin-field">
              <label className="admin-label" htmlFor="new_password">
                New password
              </label>
              <PasswordInput
                id="new_password"
                classPrefix="admin"
                autoComplete="new-password"
                aria-invalid={errors.new_password ? 'true' : 'false'}
                {...register('new_password', {
                  required: 'Enter a new password',
                  minLength: {
                    value: 12,
                    message: 'At least 12 characters',
                  },
                })}
              />
              {errors.new_password && (
                <p className="admin-field-error">
                  {errors.new_password.message}
                </p>
              )}
              <p className="admin-hint">At least 12 characters.</p>
            </div>

            <div className="admin-field">
              <label className="admin-label" htmlFor="confirm_password">
                Confirm new password
              </label>
              <PasswordInput
                id="confirm_password"
                classPrefix="admin"
                autoComplete="new-password"
                {...register('confirm_password', {
                  required: 'Confirm your new password',
                })}
              />
              {watch('confirm_password') &&
                watch('confirm_password') !== newPassword && (
                  <p className="admin-field-error">Passwords do not match.</p>
                )}
            </div>

            <div className="admin-form-actions">
              <button
                type="submit"
                className="admin-btn admin-btn-primary"
                disabled={isSubmitting || changePassword.isPending}
              >
                {changePassword.isPending ? 'Saving…' : 'Change password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  )
}
